import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { X, UserPlus, Trash2, Shield, UserCog, Copy, CheckCircle2, Mail, MailX } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ROLES = [
  { value: "admin", label: "Admin" },
  { value: "trainer", label: "Trainer" },
  { value: "front_desk", label: "Front Desk" },
];

export default function StaffManagementModal({ onClose, currentUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "trainer", sendEmail: true });
  const [submitting, setSubmitting] = useState(false);
  const [lastInvite, setLastInvite] = useState(null); // { email, tempPassword, emailSent, emailSkipped }

  async function refresh() {
    try {
      const res = await api.get("/users");
      setUsers(res.data);
    } catch (err) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  async function inviteUser(e) {
    e.preventDefault();
    if (!form.email) {
      toast.error("Email is required");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/users/invite", form);
      setLastInvite({
        email: res.data.user.email,
        tempPassword: res.data.tempPassword,
        emailSent: res.data.emailSent,
        emailSkipped: res.data.emailSkipped,
      });
      toast.success(
        res.data.emailSent
          ? `Invite emailed to ${res.data.user.email}`
          : `User created. Share the temp password manually.`
      );
      setForm({ email: "", name: "", role: "trainer", sendEmail: true });
      setShowInvite(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to invite user");
    } finally {
      setSubmitting(false);
    }
  }

  function copyTempPassword() {
    if (!lastInvite?.tempPassword) return;
    try {
      navigator.clipboard?.writeText(lastInvite.tempPassword);
      toast.success("Temporary password copied");
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  }

  async function changeRole(u, role) {
    try {
      await api.put(`/users/${u.id}`, { role });
      toast.success("Role updated");
      refresh();
    } catch (err) {
      toast.error("Failed to update role");
    }
  }

  async function resetPassword(u) {
    const newPw = window.prompt(`Set new password for ${u.email}:`);
    if (!newPw) return;
    try {
      await api.put(`/users/${u.id}`, { password: newPw });
      toast.success("Password updated");
    } catch (err) {
      toast.error("Failed to update password");
    }
  }

  async function deleteUser(u) {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to delete user");
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="user-mgmt-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-h-[92vh] w-full max-w-3xl overflow-y-auto rounded-3xl bg-white p-6 shadow-xl"
      >
        <div className="mb-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserCog size={22} />
            <h2 className="text-2xl font-bold">Manage Staff</h2>
          </div>
          <Button variant="outline" onClick={onClose} className="rounded-2xl" data-testid="close-users-btn">
            <X size={18} />
          </Button>
        </div>

        {lastInvite && (
          <div
            data-testid="invite-result"
            className="mb-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4"
          >
            <div className="flex items-start gap-2">
              <CheckCircle2 size={18} className="mt-0.5 flex-shrink-0 text-emerald-600" />
              <div className="flex-1">
                <p className="font-semibold text-emerald-900">Invite ready for {lastInvite.email}</p>
                <p className="mt-0.5 text-sm text-emerald-800">
                  {lastInvite.emailSent ? (
                    <><Mail size={12} className="-mt-0.5 mr-1 inline" />Email sent automatically.</>
                  ) : (
                    <><MailX size={12} className="-mt-0.5 mr-1 inline" />Email not configured — share this temporary password manually:</>
                  )}
                </p>
                <div className="mt-2 flex items-center gap-2">
                  <code
                    data-testid="invite-temp-password"
                    className="rounded-lg bg-white px-3 py-1.5 text-sm font-mono text-slate-900 ring-1 ring-emerald-200"
                  >
                    {lastInvite.tempPassword}
                  </code>
                  <Button
                    data-testid="copy-temp-password-btn"
                    type="button"
                    variant="outline"
                    onClick={copyTempPassword}
                    className="rounded-xl"
                    size="sm"
                  >
                    <Copy size={13} className="mr-1" />
                    Copy
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setLastInvite(null)}
                    size="sm"
                    className="ml-auto rounded-xl text-emerald-900 hover:bg-emerald-100"
                  >
                    Dismiss
                  </Button>
                </div>
                <p className="mt-2 text-xs text-emerald-700">
                  Ask the new user to log in and change their password right away.
                </p>
              </div>
            </div>
          </div>
        )}

        {!showInvite ? (
          <Button
            data-testid="open-invite-user-btn"
            onClick={() => setShowInvite(true)}
            className="mb-4 rounded-2xl"
          >
            <UserPlus size={16} className="mr-2" />
            Invite User
          </Button>
        ) : (
          <form onSubmit={inviteUser} className="mb-4 grid gap-3 rounded-2xl border p-4 md:grid-cols-2">
            <label className="space-y-1 text-sm font-medium">
              Email
              <input
                data-testid="invite-user-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full rounded-xl border p-2 font-normal"
              />
            </label>
            <label className="space-y-1 text-sm font-medium">
              Name (optional)
              <input
                data-testid="invite-user-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full rounded-xl border p-2 font-normal"
              />
            </label>
            <label className="space-y-1 text-sm font-medium md:col-span-1">
              Role
              <select
                data-testid="invite-user-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full rounded-xl border p-2 font-normal"
              >
                {ROLES.map((r) => (
                  <option key={r.value} value={r.value}>
                    {r.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="flex items-end gap-2 text-sm">
              <input
                data-testid="invite-send-email"
                type="checkbox"
                checked={form.sendEmail}
                onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })}
                className="h-4 w-4 rounded"
              />
              <span>Email the invite (auto-skipped if Resend isn't configured)</span>
            </label>
            <div className="flex gap-2 md:col-span-2">
              <Button data-testid="send-invite-btn" type="submit" disabled={submitting} className="rounded-2xl">
                {submitting ? "Sending…" : "Send invite"}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowInvite(false)} className="rounded-2xl">
                Cancel
              </Button>
            </div>
            <p className="text-xs text-slate-500 md:col-span-2">
              A secure temporary password is generated automatically. You'll see it after the invite is created and
              can copy it to share manually if needed.
            </p>
          </form>
        )}

        {loading ? (
          <p className="text-slate-500">Loading...</p>
        ) : (
          <div className="space-y-2" data-testid="users-list">
            {users.map((u) => (
              <div
                key={u.id}
                className="flex flex-col gap-2 rounded-2xl border p-3 md:flex-row md:items-center md:justify-between"
              >
                <div className="flex items-center gap-3">
                  <Shield size={18} className="text-slate-500" />
                  <div>
                    <p className="font-medium">{u.name || u.email}</p>
                    <p className="text-sm text-slate-500">{u.email}</p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <select
                    data-testid={`role-select-${u.id}`}
                    value={u.role}
                    onChange={(e) => changeRole(u, e.target.value)}
                    className="rounded-xl border p-2 text-sm"
                  >
                    {ROLES.map((r) => (
                      <option key={r.value} value={r.value}>
                        {r.label}
                      </option>
                    ))}
                  </select>
                  <Button variant="outline" onClick={() => resetPassword(u)} className="rounded-xl">
                    Reset Password
                  </Button>
                  {u.id !== currentUser?.id && (
                    <Button
                      variant="outline"
                      onClick={() => deleteUser(u)}
                      data-testid={`delete-user-${u.id}`}
                      className="rounded-xl"
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                  {u.id === currentUser?.id && (
                    <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-600">You</span>
                  )}
                </div>
              </div>
            ))}
            {users.length === 0 && <p className="text-slate-500">No users yet.</p>}
          </div>
        )}

        <p className="mt-4 text-xs text-slate-500">
          <span className="font-semibold">Roles:</span> Admin = full access + user management • Trainer = full
          client/session/payment • Front Desk = schedule & record payments only
        </p>
      </motion.div>
    </div>
  );
}
